/**
 * Detail-node injection for the provenance DAG.
 *
 * For each parent node whose ID is in `expandedDetails`, reads the
 * `value_json` entries from the underlying DAGNode and generates
 * synthetic "detailPill" nodes + dashed edges.
 */

import type { Node, Edge } from "@xyflow/react";
import type { DAGNode } from "../../api/artifacts";

/** Keys to exclude — internal IDs and fields that aren't useful on the graph. */
const EXCLUDED_KEYS = new Set([
  "flow360_case_id",
  "flow360_geometry_id",
  "flow360_surface_mesh_id",
  "flow360_volume_mesh_id",
  "flow360_folder_id",
  "base_case_id",
  "sweep_id",
  "sweep_params",
]);

function formatDetailValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") {
    return Number.isInteger(v) ? String(v) : v.toPrecision(6).replace(/\.?0+$/, "");
  }
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

export interface DetailInjectionResult {
  nodes: Node[];
  edges: Edge[];
}

/**
 * Inject synthetic detail pill nodes for every parent in `expandedDetails`.
 *
 * Returns a new node/edge array with the originals plus the pills and
 * dashed connecting edges.
 */
export function injectDetailNodes(
  nodes: Node[],
  edges: Edge[],
  expandedDetails: Set<string>,
): DetailInjectionResult {
  if (expandedDetails.size === 0) return { nodes, edges };

  const extraNodes: Node[] = [];
  const extraEdges: Edge[] = [];

  for (const node of nodes) {
    if (!expandedDetails.has(node.id)) continue;

    const dagNode = node.data?.dagNode as DAGNode | undefined;
    const valueJson = dagNode?.value_json;
    if (!valueJson || typeof valueJson !== "object") continue;

    const entries = Object.entries(valueJson).filter(
      ([k, v]) => !EXCLUDED_KEYS.has(k) && v !== null && v !== undefined && v !== "",
    );

    for (const [key, value] of entries) {
      const pillId = `detail-${node.id}-${key}`;

      extraNodes.push({
        id: pillId,
        type: "detailPill",
        data: {
          detailKey: key,
          detailValue: formatDetailValue(value),
        },
        position: { x: 0, y: 0 },
      });

      extraEdges.push({
        id: `dedge-${node.id}-${key}`,
        source: node.id,
        target: pillId,
        style: { stroke: "rgba(148,163,184,0.4)", strokeDasharray: "4 3" },
        animated: false,
      });
    }
  }

  return {
    nodes: [...nodes, ...extraNodes],
    edges: [...edges, ...extraEdges],
  };
}
